import { Link, useParams, Navigate } from "react-router-dom";
import { SERVICES } from "@/data/site";
import * as LI from "lucide-react";
import { ArrowRight, Check, ArrowLeft, Sparkles } from "lucide-react";
import Seo, { ORG_SCHEMA, SITE_URL } from "@/components/Seo";
import CityLinks from "@/components/CityLinks";

const iconMap = {
  megaphone: LI.Megaphone, monitor: LI.Monitor, kanban: LI.KanbanSquare,
  "share-2": LI.Share2, palette: LI.Palette, lightbulb: LI.Lightbulb,
  shield: LI.ShieldCheck, "graduation-cap": LI.GraduationCap,
};

// Placeholder process for each service
const PROCESS_STEPS = [
  { t: "Discovery", d: "We dive into your goals, audience and current setup to map opportunities." },
  { t: "Strategy", d: "We share a clear roadmap with deliverables, timelines and expected outcomes." },
  { t: "Execution", d: "Our team ships the work — design, code, campaigns or hardware — with weekly reviews." },
  { t: "Growth", d: "We measure, report and iterate to keep improving results month after month." },
];

const FAQ_TEMPLATES = {
  default: [
    { q: "How long does a typical project take?", a: "Most projects wrap in 3–6 weeks depending on scope. We'll share a firm timeline after the discovery call." },
    { q: "How much does it cost?", a: "Every project is scoped individually. Book a free consultation and we'll share a transparent estimate within 48 hours." },
    { q: "Do you offer ongoing support?", a: "Yes — we offer monthly retainers for maintenance, growth, and monitoring on every service we deliver." },
  ],
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;
  const Icon = iconMap[service.icon] || LI.Sparkles;

  // Related services (2 others)
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`service-detail-${slug}`}>
      <Seo
        title={`${service.title} — Services`}
        description={service.long.slice(0, 158)}
        image={service.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: service.title,
          description: service.long,
          image: service.image,
          url: `${SITE_URL}/services/${service.slug}`,
          areaServed: "IN",
          provider: ORG_SCHEMA,
        }}
      />

      {/* HERO */}
      <section className="hero-gradient text-white overflow-hidden">
        <div className="container-x py-16 md:py-20">
          <Link to="/services" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm" data-testid="back-to-services">
            <ArrowLeft className="w-4 h-4" /> All Services
          </Link>
          <div className="mt-6 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="chip-light"><Sparkles className="w-3.5 h-3.5" /> Service</div>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-[color:var(--dh-orange)] text-white grid place-items-center"><Icon className="w-7 h-7" /></div>
              </div>
              <h1 className="mt-4 text-4xl md:text-6xl">{service.title}</h1>
              <p className="mt-5 text-white/80 max-w-xl leading-relaxed">{service.long}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/contact" className="btn-primary" data-testid="service-hero-cta">Enquire Now <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/services" className="btn-outline">All Services</Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-[color:var(--dh-orange)]/30 blur-2xl" />
              <img src={service.image} alt={service.title} className="relative rounded-3xl border border-white/15 w-full h-[380px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="container-x py-20">
        <div className="grid md:grid-cols-3 gap-10">
          <div>
            <div className="eyebrow">What's Included</div>
            <h2 className="mt-3 text-3xl md:text-4xl text-[color:var(--dh-navy)]">Deliverables tailored to your business.</h2>
            <p className="mt-4 text-[color:var(--dh-slate)] leading-relaxed">Every {service.title.toLowerCase()} engagement includes strategy, execution and reporting so you always know what's happening and why.</p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
            {service.bullets.map((b) => (
              <div key={b} className="p-5 rounded-2xl border border-[color:var(--dh-line)] bg-white flex gap-3">
                <Check className="w-5 h-5 mt-0.5 text-[color:var(--dh-orange)] shrink-0" />
                <div className="font-display font-bold text-[color:var(--dh-navy)]">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-[color:var(--dh-cream)] py-20">
        <div className="container-x">
          <div className="text-center">
            <div className="eyebrow justify-center">Our Process</div>
            <h2 className="mt-3 text-3xl md:text-4xl text-[color:var(--dh-navy)]">A four-step engagement.</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-4 gap-5">
            {PROCESS_STEPS.map((s, i) => (
              <div key={s.t} className="p-6 rounded-2xl border border-[color:var(--dh-line)] bg-white relative">
                <div className="absolute -top-3 -left-3 w-9 h-9 rounded-full bg-[color:var(--dh-orange)] text-white grid place-items-center font-display font-bold">{i + 1}</div>
                <div className="mt-2 font-display text-lg text-[color:var(--dh-navy)]">{s.t}</div>
                <p className="mt-2 text-sm text-[color:var(--dh-slate)]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE STUDY placeholder */}
      <section className="container-x py-20">
        <div className="rounded-3xl border-2 border-dashed border-[color:var(--dh-line)] p-10 md:p-14 bg-white" data-testid="case-study-placeholder">
          <div className="eyebrow">Case Study</div>
          <h2 className="mt-3 font-display text-2xl md:text-3xl text-[color:var(--dh-navy)]">Real client story coming soon</h2>
          <p className="mt-3 text-[color:var(--dh-slate)] max-w-2xl">
            We're compiling a detailed case study of a recent {service.title.toLowerCase()} engagement — including the challenge, our approach and the measurable outcome. In the meantime, book a free consultation and we'll share relevant examples for your industry.
          </p>
          <Link to="/contact" className="btn-primary mt-6">Request Case Studies <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-x pb-20">
        <div className="grid md:grid-cols-[1fr_1.5fr] gap-10">
          <div>
            <div className="eyebrow">FAQs</div>
            <h2 className="mt-3 text-3xl md:text-4xl text-[color:var(--dh-navy)]">Common questions.</h2>
            <p className="mt-3 text-[color:var(--dh-slate)]">Can't find what you're looking for? <Link to="/contact" className="text-[color:var(--dh-orange)] underline font-semibold">Ask us anything.</Link></p>
          </div>
          <div className="space-y-3">
            {(FAQ_TEMPLATES[slug] || FAQ_TEMPLATES.default).map((f, i) => (
              <details key={f.q} className="group p-5 rounded-2xl border border-[color:var(--dh-line)] bg-white" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer font-display font-bold text-[color:var(--dh-navy)] flex items-center justify-between list-none">
                  {f.q} <span className="transition-transform group-open:rotate-45 text-[color:var(--dh-orange)] text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-[color:var(--dh-slate)]">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* RELATED */}
      <CityLinks service={service} />

      <section className="bg-[color:var(--dh-navy)] text-white py-20">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <h2 className="text-3xl md:text-4xl">Explore related services</h2>
            <Link to="/services" className="btn-outline">View All <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {related.map((r) => {
              const RIcon = iconMap[r.icon] || LI.Sparkles;
              return (
                <Link key={r.slug} to={`/services/${r.slug}`} className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors block" data-testid={`related-${r.slug}`}>
                  <div className="w-11 h-11 rounded-xl bg-[color:var(--dh-orange)] grid place-items-center"><RIcon className="w-5 h-5" /></div>
                  <div className="mt-3 font-display text-lg">{r.title}</div>
                  <p className="mt-1 text-sm text-white/70">{r.short}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
